import { isEmpty } from 'src/utils/general';
import { LOGIN_PARAMS } from 'src/constants';
import { encodeData, decodeData } from 'src/utils/cryptoStorage';

const DB_NAME = 'app-db';
const DB_VERSION = 1;
const READ_ONLY = 'readonly';
const READ_WRITE = 'readwrite';
const STORE_NAME = 'doc2App-objects';

const ITEMS_TO_NOT_CLEAR = [LOGIN_PARAMS];

interface LargeObject<T = any> {
  id: string;
  data: T;
  newFormate?: boolean;
}

interface ExpiringItem<T = any> {
  value: T;
  expiry: number;
}

let db: IDBDatabase | null = null;

async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (db) {
      try {
        // Try starting a dummy transaction to check if db is still alive
        db.transaction(STORE_NAME, READ_ONLY);
        return resolve(db);
      } catch (error) {
        console.warn('DB connection lost, reopening...');
        db = null; // Clear the db to reopen
      }
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      const error = (event.target as IDBRequest).error;
      logError('IndexedDB open error:', error);
      reject(error);
    };

    request.onsuccess = (event) => {
      db = (event.target as IDBRequest).result as IDBDatabase;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      db = (event.target as IDBRequest).result as IDBDatabase;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

export async function saveObjectToIndexDb<T = any>(
  id: string,
  data: T,
  encriptData = false,
): Promise<void> {
  if (!id || !data || isEmpty(data)) {
    return;
  }

  let finalData = data as any;
  if (encriptData) {
    finalData = await encodeData(data);
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, READ_WRITE);
    const store = tx.objectStore(STORE_NAME);

    // TODO last parameter will be removed before Prod Push or even if not removed no issue
    // just to cleanup old data

    const request = store.put({ id, data: finalData, newFormate: true });
    request.onsuccess = () => resolve();
    request.onerror = (e) => reject((e.target as IDBRequest).error);
  });
}

export async function getObjectFromIndexDb<T = any>(
  id: string,
  decriptData = false,
): Promise<T | null> {
  if (!id) {
    return null;
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, READ_ONLY);
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = async () => {
      const result = request.result as LargeObject<T> | undefined;

      let finalData = result?.data ?? (null as any);

      // TODO will be removed before Prod Push or even if not removed no issue
      // just to cleanup old data
      if (!result?.newFormate) {
        finalData = null;
      }

      if (finalData && decriptData) {
        finalData = await decodeData(finalData);
      }

      resolve(finalData);
    };

    request.onerror = (e) => reject((e.target as IDBRequest).error);
  });
}

export async function deleteObjectFromIndexDb(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, READ_WRITE);
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = (e) => reject((e.target as IDBRequest).error);
  });
}

export async function clearAllObjectsFromIndex(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, READ_WRITE);
    const store = tx.objectStore(STORE_NAME);

    // First, get all items that should be preserved
    const itemsToPreserve: Map<string, LargeObject> = new Map();
    const getAllRequest = store.getAll();

    getAllRequest.onsuccess = () => {
      const allItems = getAllRequest.result as LargeObject[];

      // Save items that should not be cleared
      allItems.forEach((item) => {
        if (ITEMS_TO_NOT_CLEAR.includes(item.id)) {
          itemsToPreserve.set(item.id, item);
        }
      });

      // Clear all items
      const clearRequest = store.clear();

      clearRequest.onsuccess = () => {
        // Restore preserved items
        if (itemsToPreserve.size === 0) {
          resolve();
          return;
        }

        let restoreCount = 0;
        itemsToPreserve.forEach((item) => {
          const putRequest = store.put(item);
          putRequest.onsuccess = () => {
            restoreCount++;
            if (restoreCount === itemsToPreserve.size) {
              resolve();
            }
          };
          putRequest.onerror = (e) => reject((e.target as IDBRequest).error);
        });
      };

      clearRequest.onerror = (e) => reject((e.target as IDBRequest).error);
    };

    getAllRequest.onerror = (e) => reject((e.target as IDBRequest).error);
  });
}

// 📦 Save object with expiry
export async function saveObjectToIndexDbWithExpiry<T = any>(
  id: string,
  data: T,
  encriptData = false,
  expiryHrs = 4,
): Promise<void> {
  if (!id || !data || isEmpty(data)) {
    return;
  }

  const expiry = getExpireTime(expiryHrs);

  const item: ExpiringItem<T> = {
    value: data,
    expiry,
  };

  await saveObjectToIndexDb(id, item, encriptData);
}

// 🔍 Get object with expiry check
export async function getObjectFromIndexDbWithExpiry<T = any>(
  id: string,
  isDataEncripted = false,
  returnExpiryStatus = false,
): Promise<T | { value: T | null; isDataExpired: boolean } | null> {
  const item = await getObjectFromIndexDb<ExpiringItem<T>>(id, isDataEncripted);

  if (!item) {
    return null;
  }

  const now = Date.now();

  if (now > item.expiry) {
    const data = item.value;
    await deleteObjectFromIndexDb(id);

    if (returnExpiryStatus) {
      return { value: data, isDataExpired: true };
    }
    return null;
  }

  if (returnExpiryStatus) {
    return { value: item.value, isDataExpired: false };
  }

  return item.value;
}

// 🕰️ Helper to calculate expiry (optional if you want custom times)
export function getExpireTime(hoursFromNow = 4): number {
  return Date.now() + hoursFromNow * 60 * 60 * 1000;
}
